import React, { useRef, useEffect, useState } from 'react';
import { BarcodeScanner } from './BarcodeScanner';

interface SkuScannerProps {
  onSubmit: (sku: string) => void;
  onClear: () => void;
  loading: boolean;
}

export function SkuScanner({ onSubmit, onClear, loading }: SkuScannerProps) {
  const [value, setValue] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim()) {
      onSubmit(value.trim());
    }
  };

  const handleClear = () => {
    setValue('');
    onClear();
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleBarcodeDetected = (code: string) => {
    setCameraOpen(false);
    setValue(code);
    // Defer lookup to next frame so the modal unmounts first
    requestAnimationFrame(() => onSubmit(code.trim()));
  };

  return (
    <div style={styles.container}>
      <div style={styles.labelRow}>
        <span style={styles.icon}>&#x25A6;</span>
        <span style={styles.label}>Scan or type a SKU barcode</span>
      </div>
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.inputRow}>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. SKU-12345-BLU-M"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            disabled={loading}
            style={{
              ...styles.input,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'text',
            }}
            aria-label="SKU input"
          />
          <button
            type="button"
            onClick={() => setCameraOpen(true)}
            disabled={loading}
            style={{
              ...styles.cameraButton,
              opacity: loading ? 0.5 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            aria-label="Scan with camera"
            title="Scan with camera"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </button>
        </div>
        <div style={styles.buttons}>
          <button
            type="submit"
            disabled={loading || !value.trim()}
            style={{
              ...styles.button,
              ...styles.submitButton,
              opacity: loading || !value.trim() ? 0.5 : 1,
              cursor: loading || !value.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? (
              <span style={styles.spinner} />
            ) : (
              'Look Up'
            )}
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={loading}
            style={{
              ...styles.button,
              ...styles.clearButton,
              opacity: loading ? 0.5 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            Clear
          </button>
        </div>
      </form>

      {cameraOpen && (
        <BarcodeScanner
          onDetected={handleBarcodeDetected}
          onClose={() => setCameraOpen(false)}
        />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    maxWidth: '720px',
    margin: '0 auto',
    padding: '0 16px',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '14px',
  },
  icon: {
    fontSize: '22px',
    color: '#a0aec0',
  },
  label: {
    fontSize: '18px',
    fontWeight: 500,
    color: '#a0aec0',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  inputRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'stretch',
  },
  input: {
    flex: 1,
    minWidth: 0,
    padding: '20px 24px',
    fontSize: '28px',
    fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
    fontWeight: 600,
    letterSpacing: '0.05em',
    background: '#1a1a1a',
    color: '#f7fafc',
    border: '2px solid #2d3748',
    borderRadius: '12px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease',
  },
  cameraButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '72px',
    flexShrink: 0,
    background: '#1a1a1a',
    color: '#a0aec0',
    border: '2px solid #2d3748',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, color 0.15s ease',
  },
  buttons: {
    display: 'flex',
    gap: '12px',
  },
  button: {
    padding: '14px 32px',
    fontSize: '18px',
    fontWeight: 600,
    border: 'none',
    borderRadius: '10px',
    transition: 'background 0.15s ease, transform 0.1s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '120px',
  },
  submitButton: {
    flex: 1,
    background: '#3182ce',
    color: '#fff',
  },
  clearButton: {
    background: '#2d3748',
    color: '#a0aec0',
  },
  spinner: {
    display: 'inline-block',
    width: '20px',
    height: '20px',
    border: '3px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
};
